import { useMemo, useCallback } from "react"
import { map, chain, compact } from "lodash"
import { useOutletContext } from "react-router-dom"
import { percentageFormatter, oneDecimalPercentageFormatter } from "helpers/formatters/numbers"
import { createPerformanceCumulativeSingleDateQuery } from "gatherers/single-date/cumulative-performance"
import { createPerformanceCumulativeMultiDateQuery, getMetrics } from "gatherers/multi-date/cumulative-performance"
import { createPeriodPerformanceSingleDateQuery, ANNUALIZED_RETURNS, ANNUALIZED_VOLATILITY, MAX_DRAWDOWN, RETURNS_CORRELATION, SHARPE_RATIO, SORTINO_RATIO, FACTORS_ANNUALIZED_RETURNS, FACTORS_ANNUALIZED_VOLATILITY, FACTORS_SHARPE_RATIO, ALPHA_ANNUALIZED_RETURNS, ALPHA_ANNUALIZED_VOLATILITY, ALPHA_SHARPE_RATIO, TOTAL_RETURN, ALPHA_RETURN, FACTORS_RETURN, TRADING_RETURN, FACTOR_ANNUALIZED_RETURNS, FACTOR_ANNUALIZED_VOLATILITY, FACTOR_SHARPE_RATIO, BATTING_ALPHA, BATTING_TOTAL, BATTING_FACTORS, SLUGGING_ALPHA, SLUGGING_TOTAL, SLUGGING_FACTORS } from "gatherers/single-date/period-performance"
import { useIsBattingSluggingEnabled, useIsRfrAttributionEnabled } from "resources/account"
import CompareGraph from "../renderers/compare-graph"
import { useSelectedMetric } from "../hooks/selected-metric"
import { useAvailableMetrics } from "../hooks/available-metrics"
import DataIntegratedSection from "./data-integrated-section"
import { transformDecisionQualityLabels } from "../helpers/transform-decision-quality-labels"

export default function ComparePerformance() {
  const context = useOutletContext()
  const { modelMetadata, modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric, modelMetrics, contextBase } = context
  const isBattingSluggingEnabled = useIsBattingSluggingEnabled()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()

  const specificMetrics = useMemo(() => {
    const metrics = getMetrics(isRfrAttributionEnabled)
    return {
      id: "cumulative",
      label: "Cumulative Performance",
      options: map(metrics, ({ label }, id) => ({ label, id, category: "cumulative" })),
    }
  }, [isRfrAttributionEnabled])
  const availableMetrics = useAvailableMetrics(modelMetrics, specificMetrics, true)
  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)
  const isFactorMetric = useCallback((metric) => {
    return chain(modelMetrics)
      .map(({ options }) => options)
      .flatten()
      .find(({ id }) => id === metric.id)
      .value()
  }, [modelMetrics])

  const currentDate = selectedDate || endDate
  // Memoize categories with fallback to prevent runtime errors if modelMetadata is undefined,
  // and avoid creating a new empty array reference on each render which would invalidate downstream useMemo
  const categories = useMemo(() => modelMetadata?.categories ?? [], [modelMetadata?.categories])

  const battingSluggingTotalMetrics = useMemo(
    () => (isBattingSluggingEnabled ? [BATTING_TOTAL, SLUGGING_TOTAL] : []),
    [isBattingSluggingEnabled],
  )
  const battingSluggingAlphaMetrics = useMemo(
    () => (isBattingSluggingEnabled ? [BATTING_ALPHA, SLUGGING_ALPHA] : []),
    [isBattingSluggingEnabled],
  )
  const battingSluggingFactorsMetrics = useMemo(
    () => (isBattingSluggingEnabled ? [BATTING_FACTORS, SLUGGING_FACTORS] : []),
    [isBattingSluggingEnabled],
  )

  const sections = useMemo(() => {
    const graphSection = {
      id: "graph",
      name: "Graph",
      renderer: CompareGraph,
      metrics: map(positionSets, positionSet =>
        createPerformanceCumulativeMultiDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          startDate,
          endDate,
          false,
          { isRfrAttributionEnabled },
        ),
      ),
      availableMetrics,
      selectedMetric,
      positionSetsMetadata: positionSets,
      tickFormatter: oneDecimalPercentageFormatter,
      separator: true,
    }

    const totalSection = {
      id: "total",
      name: "Total Return",
      defaultExpanded: true,
      separator: true,
      dataTransform: transformDecisionQualityLabels,
      metrics: map(positionSets, positionSet =>
        createPeriodPerformanceSingleDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          positionSets[0].queryPositionSet,
          startDate,
          currentDate,
          [
            TOTAL_RETURN,
            ANNUALIZED_RETURNS,
            ANNUALIZED_VOLATILITY,
            SHARPE_RATIO,
            SORTINO_RATIO,
            RETURNS_CORRELATION,
            MAX_DRAWDOWN,
            ...battingSluggingTotalMetrics,
          ],
          { contextBase, isRfrAttributionEnabled },
        ),
      ),
      rowCount: 7 + battingSluggingTotalMetrics.length,
    }

    const alphaSection = {
      id: "alpha",
      name: "Alpha",
      dataTransform: transformDecisionQualityLabels,
      metrics: map(positionSets, positionSet =>
        createPeriodPerformanceSingleDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          positionSets[0].queryPositionSet,
          startDate,
          currentDate,
          [
            ALPHA_RETURN,
            ALPHA_ANNUALIZED_RETURNS,
            ALPHA_ANNUALIZED_VOLATILITY,
            ALPHA_SHARPE_RATIO,
            ...battingSluggingAlphaMetrics,
          ],
          { contextBase, isRfrAttributionEnabled },
        ),
      ),
      rowCount: 4 + battingSluggingAlphaMetrics.length,
    }

    const factorsSection = {
      id: "factors",
      name: "Factors",
      separator: true,
      dataTransform: transformDecisionQualityLabels,
      metrics: map(positionSets, positionSet =>
        createPeriodPerformanceSingleDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          positionSets[0].queryPositionSet,
          startDate,
          currentDate,
          [
            FACTORS_RETURN,
            FACTORS_ANNUALIZED_RETURNS,
            FACTORS_ANNUALIZED_VOLATILITY,
            FACTORS_SHARPE_RATIO,
            ...battingSluggingFactorsMetrics,
          ],
          { contextBase, isRfrAttributionEnabled },
        ),
      ),
      rowCount: 4 + battingSluggingFactorsMetrics.length,
    }

    const tradingSection = {
      id: "trading",
      name: "Trading",
      separator: !isRfrAttributionEnabled,
      metrics: map(positionSets, positionSet =>
        createPeriodPerformanceSingleDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          positionSets[0].queryPositionSet,
          startDate,
          currentDate,
          [TRADING_RETURN],
          { contextBase, isRfrAttributionEnabled },
        ),
      ),
    }

    const selectedFactorSection = isFactorMetric(selectedMetric) && {
      id: `selected-metric-${selectedMetric.id}`,
      name: selectedMetric.label,
      downloadName: `${selectedMetric.label} Performance`,
      separator: true,
      metrics: map(positionSets, positionSet =>
        createPeriodPerformanceSingleDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          positionSets[0].queryPositionSet,
          startDate,
          currentDate,
          [
            FACTOR_ANNUALIZED_RETURNS,
            FACTOR_ANNUALIZED_VOLATILITY,
            FACTOR_SHARPE_RATIO,
          ],
          { factorId: selectedMetric.id, isRfrAttributionEnabled },
        ),
      ),
    }

    const categorySections = map(categories, category => ({
      id: category.id,
      name: category.label,
      color: category.color,
      isGroup: true,
      sorted: true,
      metrics: map(positionSets, positionSet =>
        createPerformanceCumulativeSingleDateQuery(
          modelId,
          positionSet.queryPositionSet,
          positionSet.queryBase,
          startDate,
          currentDate,
          { category: category.id },
        ),
      ),
    }))

    return compact([
      graphSection,
      totalSection,
      alphaSection,
      factorsSection,
      tradingSection,
      selectedFactorSection,
      ...categorySections,
    ])
  },
  // [TODO] how to improve reference integrity of position sets
  [positionSets, availableMetrics, selectedMetric, isFactorMetric, categories, modelId, startDate, endDate, currentDate, contextBase, battingSluggingTotalMetrics, battingSluggingAlphaMetrics, battingSluggingFactorsMetrics, isRfrAttributionEnabled],
  )

  return sections.map((section, index) => {
    const configKey = `performance-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={percentageFormatter}
      />
    )
  })
}
